import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ClipboardList, CheckCircle2, Clock, ArrowRight, Lock } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

export default function MySurveysPage() {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/surveys/my-all`, { headers: getAuthHeader() });
      if (r.ok) setRows(await r.json());
    } catch (_e) { toast.error('Could not load your surveys'); }
    finally { setLoading(false); }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const pending = rows.filter(r => r.can_fill);
  const completed = rows.filter(r => !r.can_fill);

  const Row = ({ s }) => (
    <div className="border rounded-lg p-4 flex items-start justify-between gap-3 flex-wrap" data-testid={`my-survey-${s.survey_id}`}>
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-medium">{s.title}</p>
          {s.is_mandatory && <Badge className="bg-rose-600 text-white hover:bg-rose-600 text-[10px]">Mandatory</Badge>}
          {s.is_anonymous && <Badge variant="outline" className="text-[10px]"><Lock className="h-3 w-3 mr-1" /> Anonymous</Badge>}
        </div>
        {s.description && <p className="text-sm text-muted-foreground line-clamp-2 mt-0.5">{s.description}</p>}
        <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
          <span>{s.question_count} question{s.question_count === 1 ? '' : 's'}</span>
          {s.expires_at && <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> Closes {fmtDate(s.expires_at)}</span>}
          {s.submitted && s.submitted_at && <span className="text-emerald-600">Submitted {fmtDate(s.submitted_at)}</span>}
        </div>
      </div>
      <div className="shrink-0">
        {s.can_fill ? (
          <Button size="sm" onClick={() => navigate(`/surveys/${s.survey_id}/fill`)} data-testid={`fill-survey-${s.survey_id}`}>
            Give Feedback <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        ) : s.submitted ? (
          <Badge className="bg-violet-600 text-white hover:bg-violet-600"><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Completed</Badge>
        ) : (
          <Badge className="bg-rose-600 text-white hover:bg-rose-600">Closed</Badge>
        )}
      </div>
    </div>
  );

  return (
    <Layout>
      <div className="space-y-6 max-w-4xl mx-auto" data-testid="my-surveys-page">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><ClipboardList className="h-6 w-6" /> My Surveys</h1>
          <p className="text-muted-foreground">Surveys shared with you — respond to pending ones and review what you've completed.</p>
        </div>

        {loading ? <p className="text-muted-foreground">Loading...</p>
          : rows.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-muted-foreground" data-testid="my-surveys-empty">
              <ClipboardList className="h-10 w-10 mx-auto mb-3 opacity-40" /><p>No surveys shared with you right now.</p>
            </CardContent></Card>
          ) : (
            <>
              <Card>
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><Clock className="h-4 w-4" /> Pending ({pending.length})</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  {pending.length === 0 ? <p className="text-sm text-muted-foreground">You're all caught up — nothing to fill.</p>
                    : pending.map(s => <Row key={s.survey_id} s={s} />)}
                </CardContent>
              </Card>

              {completed.length > 0 && (
                <Card data-testid="my-surveys-completed-section">
                  <CardHeader><CardTitle className="text-base flex items-center gap-2 text-muted-foreground"><CheckCircle2 className="h-4 w-4" /> Completed &amp; Closed ({completed.length})</CardTitle></CardHeader>
                  <CardContent className="space-y-3 opacity-90">
                    {completed.map(s => <Row key={s.survey_id} s={s} />)}
                  </CardContent>
                </Card>
              )}
            </>
          )}
      </div>
    </Layout>
  );
}
